import { ATTENTION_KIND, formatMoney, type IntervalUnit } from '@klopotun/domain';
import {
  CalendarClock,
  CircleSlash,
  CreditCard,
  HandCoins,
  Sparkles,
  TrendingDown,
  TrendingUp,
  type LucideIcon,
} from 'lucide-react';
import type {
  AttentionAmount,
  AttentionItem,
  ChargeSoonAttention,
  MissedChargeAttention,
  NewSubscriptionAttention,
  PayYourselfAttention,
  PriceChangedAttention,
  YearlySoonAttention,
} from '../../../entities/attention/index.js';
import { formatAmountRange } from '../../../features/payment-view/index.js';
import {
  capitaliseFirst,
  formatDayAndMonth,
  formatDayCount,
  formatDaysAhead,
  formatUnpaidCount,
} from './day-text.js';

export type AttentionUrgency = 'now' | 'soon' | 'later';

export type AttentionDetailTone = 'danger' | 'muted';

export type AttentionDetail = {
  text: string;
  tone: AttentionDetailTone;
};

export type AttentionView = {
  title: string;
  details: AttentionDetail[];
  icon: LucideIcon;
  urgency: AttentionUrgency;
};

const INTERVAL_TEXT: Record<IntervalUnit, string> = {
  month: 'щомісяця',
  year: 'щороку',
};

const LOW_BALANCE_DETAIL: AttentionDetail = {
  text: 'На картці може не вистачити',
  tone: 'danger',
};

const STILL_NEEDED_DETAIL: AttentionDetail = {
  text: 'Ще потрібна ця підписка?',
  tone: 'muted',
};

const STOPPED_DETAIL: AttentionDetail = {
  text: 'Підписку перенесено в архів',
  tone: 'muted',
};

const amountTextOf = (amount: AttentionAmount | null): string | null => {
  if (amount === null) {
    return null;
  }

  if (amount.kind === 'range') {
    return formatAmountRange(amount, amount.currency);
  }

  return formatMoney({ amountMinor: amount.amountMinor, currency: amount.currency });
};

const withAmount = (text: string, amount: AttentionAmount | null): string => {
  const amountText = amountTextOf(amount);

  return amountText === null ? text : `${text} — ${amountText}`;
};

const chargeSoonView = (item: ChargeSoonAttention): AttentionView => ({
  title: withAmount(
    `${capitaliseFirst(formatDaysAhead(item.daysLeft))} спишеться ${item.name}`,
    item.amount,
  ),
  details: item.isLowBalance ? [LOW_BALANCE_DETAIL] : [],
  icon: CreditCard,
  urgency: item.isLowBalance ? 'now' : 'soon',
});

const yearlySoonView = (item: YearlySoonAttention): AttentionView => ({
  title: withAmount(
    `${capitaliseFirst(formatDaysAhead(item.daysLeft))} річний платіж ${item.name}`,
    item.amount,
  ),
  details: item.isLowBalance ? [LOW_BALANCE_DETAIL, STILL_NEEDED_DETAIL] : [STILL_NEEDED_DETAIL],
  icon: CalendarClock,
  urgency: item.isLowBalance ? 'now' : 'soon',
});

const overdueDetailsOf = (item: PayYourselfAttention): AttentionDetail[] => {
  const isDueToday = item.daysOverdue === 0;
  const overdue: AttentionDetail[] = isDueToday
    ? []
    : [{ text: `Прострочено на ${formatDayCount(item.daysOverdue)}`, tone: 'danger' }];
  const hasMoreUnpaid = item.unpaidCount > 1;
  const moreUnpaid: AttentionDetail[] = hasMoreUnpaid
    ? [{ text: `Ще ${formatUnpaidCount(item.unpaidCount - 1)}`, tone: 'muted' }]
    : [];

  return [...overdue, ...moreUnpaid];
};

const payYourselfView = (item: PayYourselfAttention): AttentionView => ({
  title: withAmount(
    item.daysOverdue === 0 ? `Сьогодні оплатити ${item.name}` : `Оплатити ${item.name}`,
    item.amount,
  ),
  details: overdueDetailsOf(item),
  icon: HandCoins,
  urgency: 'now',
});

const missedChargeView = (item: MissedChargeAttention): AttentionView => ({
  title: `Банк не списав ${item.name} ${formatDayAndMonth(item.dueDate)}`,
  details: item.isStopped ? [STOPPED_DETAIL] : [],
  icon: CircleSlash,
  urgency: 'later',
});

const newSubscriptionView = (item: NewSubscriptionAttention): AttentionView => {
  const amountText = amountTextOf(item.amount);
  const interval = INTERVAL_TEXT[item.intervalUnit];
  const terms = amountText === null ? interval : `${amountText} ${interval}`;

  return {
    title: `Нова підписка: ${item.name} — ${terms}`,
    details:
      item.dueDate === null
        ? []
        : [{ text: `Наступне списання ${formatDayAndMonth(item.dueDate)}`, tone: 'muted' }],
    icon: Sparkles,
    urgency: 'later',
  };
};

const priceChangedView = (item: PriceChangedAttention): AttentionView => {
  const current = formatMoney({ amountMinor: item.amountMinor, currency: item.currency });
  const previous = formatMoney({ amountMinor: item.previousAmountMinor, currency: item.currency });
  const hasGrown = item.amountMinor > item.previousAmountMinor;

  return {
    title: `${item.name} тепер ${current} замість ${previous}`,
    details: [{ text: `Ціна змінилась ${formatDayAndMonth(item.changedOn)}`, tone: 'muted' }],
    icon: hasGrown ? TrendingUp : TrendingDown,
    urgency: 'later',
  };
};

export const attentionViewOf = (item: AttentionItem): AttentionView => {
  switch (item.kind) {
    case ATTENTION_KIND.CHARGE_SOON:
      return chargeSoonView(item);
    case ATTENTION_KIND.YEARLY_SOON:
      return yearlySoonView(item);
    case ATTENTION_KIND.PAY_YOURSELF:
      return payYourselfView(item);
    case ATTENTION_KIND.MISSED_CHARGE:
      return missedChargeView(item);
    case ATTENTION_KIND.NEW_SUBSCRIPTION:
      return newSubscriptionView(item);
    case ATTENTION_KIND.PRICE_CHANGED:
      return priceChangedView(item);
  }
};
